const isRealTag = (part: string) => part.length > 0 && part !== "-";

/** Clé de comparaison pour éviter les doublons (espaces / casse). */
export const normalizeModifiableTagKey = (value: string) =>
  value.trim().toLocaleLowerCase("fr");

/**
 * Déduplique une liste de tags en gardant la forme canonique du vocabulaire
 * quand elle existe (sinon la 1re occurrence trimée).
 */
export function dedupeModifiableTags(
  values: readonly string[],
  vocabulary: readonly string[] = [],
): string[] {
  const canonicalByKey = new Map<string, string>();
  for (const option of vocabulary) {
    const trimmed = option.trim();
    if (!isRealTag(trimmed)) continue;
    const key = normalizeModifiableTagKey(trimmed);
    if (!canonicalByKey.has(key)) {
      canonicalByKey.set(key, trimmed);
    }
  }

  const result: string[] = [];
  const seen = new Set<string>();
  for (const value of values) {
    if (typeof value !== "string") continue;
    const trimmed = value.trim();
    if (!isRealTag(trimmed)) continue;
    const key = normalizeModifiableTagKey(trimmed);
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(canonicalByKey.get(key) ?? trimmed);
  }
  return result;
}

/**
 * Split a stored multi-select string into tags.
 * Separators (`,`, `;`, ` - `) are never returned as tags.
 * When no explicit separator is present, greedily match known vocabulary
 * values (longest first) so space-joined legacy keywords stay intact.
 */
export function parseModifiableMultiSelectValue(
  value: unknown,
  vocabulary: readonly string[] = [],
): string[] {
  let parsed: string[];
  if (Array.isArray(value)) {
    parsed = value.filter(
      (v): v is string => typeof v === "string" && isRealTag(v.trim()),
    );
  } else if (typeof value !== "string" || value.trim() === "") {
    return [];
  } else {
    const raw = value.trim();

    if (raw.includes(",") || raw.includes(";")) {
      parsed = raw
        .split(/[,;]/)
        .map((part) => part.trim())
        .filter(isRealTag);
    } else if (raw.includes(" - ")) {
      // Legacy keywords often used " - " between multi-word tags.
      parsed = raw
        .split(" - ")
        .map((part) => part.trim())
        .filter(isRealTag);
    } else if (vocabulary.length > 0) {
      const sorted = [...vocabulary]
        .filter(isRealTag)
        .sort((a, b) => b.length - a.length);
      const found: string[] = [];
      let remaining = raw;
      while (remaining.length > 0) {
        const match = sorted.find(
          (option) =>
            remaining === option || remaining.startsWith(`${option} `),
        );
        if (!match) {
          // Unmatched residue kept as one tag (do not split on spaces).
          found.push(remaining);
          break;
        }
        found.push(match);
        remaining = remaining.slice(match.length).trim();
      }
      parsed = found.length > 0 ? found : [raw];
    } else {
      parsed = [raw];
    }
  }

  return dedupeModifiableTags(parsed, vocabulary);
}

export function joinModifiableMultiSelectValue(values: string[]): string {
  return dedupeModifiableTags(values).join(", ");
}
